import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import CaseStudiesDialog from "@/components/CaseStudiesDialog";
import { ArrowLeft, CheckCircle, Zap, Users, Target, TrendingUp } from "lucide-react";
import { 
  Heart,
  CreditCard,
  ShoppingCart,
  Store,
  Phone,
  Radio,
  Zap as Energy,
  Car,
  GraduationCap,
  Home,
  Factory,
  Truck,
  Clapperboard,
  Package,
  Power
} from "lucide-react";

// Import industry images
import healthcareImg from "@/assets/healthcare-industry.jpg";
import fintechImg from "@/assets/fintech-industry.jpg";
import ecommerceImg from "@/assets/ecommerce-industry.jpg";
import retailImg from "@/assets/retail-industry.jpg";
import telecomImg from "@/assets/telecom-industry.jpg";
import mediaImg from "@/assets/media-industry.jpg";
import energyImg from "@/assets/energy-industry.jpg";
import automotiveImg from "@/assets/automotive-industry.jpg";
import educationImg from "@/assets/education-industry.jpg";
import realEstateImg from "@/assets/real-estate-industry.jpg";
import manufacturingImg from "@/assets/manufacturing-industry.jpg";
import logisticsImg from "@/assets/logistics-industry.jpg";
import entertainmentImg from "@/assets/entertainment-industry.jpg";
import utilitiesImg from "@/assets/utilities-industry.jpg";
import consumerGoodsImg from "@/assets/consumer-goods-industry.jpg";

interface IndustryData {
  // icon: any;
  title: string;
  // category: string;
  description: string;
  image: string;
  overview: string;
  challenges: string[];
  solutions: string[];
  technologies: string[];
  benefits: string[];
  caseStudies: {
    title: string;
    description: string;
    results: string[];
  }[];
  stats: {
    label: string;
    value: string;
  }[];
}

const industriesData: { [key: string]: IndustryData } = {
  healthcare: {
    // icon: Heart,
    title: "Healthcare",
    // category: "Healthcare & Life Sciences",
    description: "Transform healthcare delivery with innovative digital solutions for patient care and medical operations.",
    image: healthcareImg,
    overview: "Our healthcare technology solutions empower medical institutions to deliver better patient care through digital transformation. We specialize in creating HIPAA-compliant systems that streamline operations, improve patient outcomes, and reduce costs while maintaining the highest security standards.",
    challenges: [
      "Complex regulatory compliance requirements (HIPAA, FDA)",
      "Integration with legacy medical systems",
      "Data security and patient privacy concerns",
      "Interoperability between different healthcare systems",
      "Scalability for growing patient populations"
    ],
    solutions: [
      "Electronic Health Records (EHR) Systems",
      "Telemedicine and Remote Care Platforms",
      "Medical Device Integration and IoT",
      "Patient Portal and Mobile Health Apps",
      "Healthcare Analytics and AI Diagnostics",
      "Practice Management Systems"
    ],
    technologies: ["HL7 FHIR", "DICOM", "Epic Integration", "Cerner", "HIPAA Compliance", "Blockchain", "AI/ML", "IoT"],
    benefits: [
      "Improved patient care and outcomes",
      "Reduced operational costs by up to 30%",
      "Enhanced data security and compliance",
      "Streamlined workflows and processes",
      "Better patient engagement and satisfaction"
    ],
    caseStudies: [
      {
        title: "Regional Hospital Network EHR Implementation",
        description: "Implemented comprehensive EHR system for 15-hospital network",
        results: ["25% reduction in patient wait times", "40% improvement in care coordination", "99.9% system uptime"]
      },
      {
        title: "Telemedicine Platform for Rural Healthcare",
        description: "Developed scalable telemedicine solution serving 50,000+ patients",
        results: ["300% increase in patient access", "60% reduction in travel costs", "95% patient satisfaction rate"]
      }
    ],
    stats: [
      { label: "Healthcare Projects", value: "6+" },
      { label: "Patient Records Managed", value: "5M+" },
      { label: "Compliance Rate", value: "100%" },
      { label: "Cost Reduction", value: "35%" }
    ]
  },
  fintech: {
    // icon: CreditCard,
    title: "FinTech",
    // category: "Financial Technology",
    description: "Build secure, scalable financial technology solutions that comply with regulatory requirements.",
    image: fintechImg,
    overview: "We deliver cutting-edge fintech solutions that revolutionize how financial services operate. Our expertise spans digital banking, payment processing, blockchain integration, and regulatory compliance, helping financial institutions stay competitive in the digital age.",
    challenges: [
      "Stringent regulatory compliance (PCI DSS, SOX, GDPR)",
      "High-security requirements for financial data",
      "Real-time transaction processing at scale",
      "Integration with banking and payment systems",
      "Fraud detection and prevention"
    ],
    solutions: [
      "Digital Banking Platforms",
      "Payment Processing Systems",
      "Blockchain and Cryptocurrency Solutions",
      "Robo-Advisors and Wealth Management",
      "RegTech and Compliance Automation",
      "Mobile Banking Applications"
    ],
    technologies: ["Blockchain", "Open Banking APIs", "Core Banking Systems", "RegTech", "AI/ML", "Microservices", "API-First"],
    benefits: [
      "Enhanced customer experience",
      "Reduced transaction costs by 40%",
      "Improved regulatory compliance",
      "Faster time-to-market for new products",
      "Scalable and secure infrastructure"
    ],
    caseStudies: [
      {
        title: "Digital Banking Platform Launch",
        description: "Built comprehensive digital banking solution for mid-tier bank",
        results: ["50% increase in digital adoption", "30% reduction in operational costs", "4.8/5 customer satisfaction"]
      },
      {
        title: "Blockchain Payment Network",
        description: "Developed cross-border payment solution using blockchain",
        results: ["80% faster international transfers", "60% lower transaction fees", "99.99% transaction success rate"]
      }
    ],
    stats: [
      { label: "Transactions Processed", value: "1B+" },
      { label: "Financial Institutions", value: "150+" },
      { label: "Security Incidents", value: "0" },
      { label: "Cost Savings", value: "40%" }
    ]
  },
  ecommerce: {
    // icon: ShoppingCart,
    title: "E-commerce",
    // category: "Digital Commerce",
    description: "Create compelling online shopping experiences with robust e-commerce platforms and solutions.",
    image: ecommerceImg,
    overview: "Our e-commerce solutions drive online business growth through scalable platforms, seamless user experiences, and powerful analytics. We help businesses of all sizes establish and expand their digital presence with cutting-edge technology.",
    challenges: [
      "Managing high traffic volumes during peak seasons",
      "Integration with multiple payment gateways",
      "Inventory management across multiple channels",
      "Mobile-first customer experience",
      "Personalization and recommendation engines"
    ],
    solutions: [
      "Custom E-commerce Platforms",
      "Multi-vendor Marketplaces",
      "Mobile Commerce Applications",
      "Payment Gateway Integration",
      "Inventory Management Systems",
      "Customer Analytics and Personalization"
    ],
    technologies: ["Shopify Plus", "WooCommerce", "Magento", "Stripe", "PayPal", "AWS", "CDN", "Elasticsearch"],
    benefits: [
      "Increased online sales by 150%",
      "Improved customer retention rates",
      "Reduced cart abandonment by 35%",
      "Enhanced mobile shopping experience",
      "Better inventory turnover"
    ],
    caseStudies: [
      {
        title: "Multi-vendor Marketplace Platform",
        description: "Built scalable marketplace supporting 1000+ vendors",
        results: ["300% increase in GMV", "50% improvement in vendor satisfaction", "99.9% platform uptime"]
      },
      {
        title: "Mobile-First Fashion Retailer",
        description: "Developed PWA for leading fashion brand",
        results: ["200% mobile conversion increase", "40% faster page load times", "60% increase in mobile traffic"]
      }
    ],
    stats: [
      { label: "E-commerce Projects", value: "10+" },
      { label: "Conversion Rate Increase", value: "150%" },
      { label: "Mobile Traffic", value: "70%" }
    ]
  },
  retail: {
    // icon: Store,
    title: "Retail",
    // category: "Retail & Consumer",
    description: "Modernize retail operations with omnichannel solutions and customer engagement platforms.",
    image: retailImg,
    overview: "We help retailers create seamless omnichannel experiences that bridge online and offline touchpoints. Our solutions enhance customer engagement, optimize operations, and drive sales growth through innovative technology.",
    challenges: [
      "Creating unified omnichannel experiences",
      "Real-time inventory synchronization",
      "Customer data integration across touchpoints",
      "Staff training and technology adoption",
      "Supply chain optimization"
    ],
    solutions: [
      "Point of Sale (POS) Systems",
      "Omnichannel Customer Experience",
      "Inventory Management Solutions",
      "Customer Loyalty Programs",
      "Staff Management Systems",
      "Supply Chain Optimization"
    ],
    technologies: ["Salesforce Commerce", "Oracle Retail", "SAP Retail", "Microsoft Dynamics", "Square", "RFID", "IoT"],
    benefits: [
      "Unified customer experience across channels",
      "30% improvement in inventory turnover",
      "Increased customer loyalty and retention",
      "Enhanced staff productivity",
      "Better demand forecasting"
    ],
    caseStudies: [
      {
        title: "National Retail Chain Omnichannel",
        description: "Implemented omnichannel solution for 80+ store chain",
        results: ["25% increase in cross-channel sales", "50% improvement in inventory accuracy", "20% higher customer satisfaction"]
      },
      {
        title: "Luxury Brand Digital Transformation",
        description: "Modernized retail operations for premium fashion brand",
        results: ["40% increase in VIP customer engagement", "35% improvement in staff efficiency", "60% growth in online-to-store visits"]
      }
    ],
    stats: [
      { label: "Retail Locations", value: "10K+" },
      { label: "Transactions Daily", value: "2M+" },
      { label: "Inventory Accuracy", value: "99.5%" },
      { label: "Customer Satisfaction", value: "4.7/5" }
    ]
  },
  telecom: {
    // icon: Phone,
    title: "Telecommunications",
    // category: "Telecom & Networking",
    description: "Enhance telecommunications infrastructure with cutting-edge network and customer management solutions.",
    image: telecomImg,
    overview: "Our telecommunications solutions enable service providers to deliver next-generation services while optimizing network performance and customer experience. We specialize in 5G, IoT, and network automation technologies.",
    challenges: [
      "5G network deployment and optimization",
      "Legacy system modernization",
      "Network security and reliability",
      "Customer service automation",
      "IoT device management at scale"
    ],
    solutions: [
      "5G Network Infrastructure",
      "Network Management Systems",
      "Customer Service Platforms",
      "Billing and Revenue Management",
      "IoT Device Management",
      "Network Security Solutions"
    ],
    technologies: ["5G", "SDN/NFV", "OSS/BSS", "VoIP", "SIP", "Network Orchestration", "Edge Computing", "AI/ML"],
    benefits: [
      "Improved network performance and reliability",
      "50% reduction in operational costs",
      "Enhanced customer experience",
      "Faster service deployment",
      "Better network security"
    ],
    caseStudies: [
      {
        title: "5G Network Rollout",
        description: "Supported nationwide 5G infrastructure deployment",
        results: ["99.9% network uptime", "40% faster data speeds", "30% reduction in latency"]
      },
      {
        title: "IoT Platform for Smart Cities",
        description: "Developed IoT management platform for urban services",
        results: ["1M+ connected devices", "25% energy savings", "50% faster incident response"]
      }
    ],
    stats: [
      { label: "Network Nodes", value: "50K+" },
      { label: "Connected Devices", value: "100M+" },
      { label: "Uptime", value: "99.99%" },
      { label: "Cost Reduction", value: "45%" }
    ]
  },
  media: {
    // icon: Radio,
    title: "Media & Entertainment",
    // category: "Media & Entertainment",
    description: "Deliver engaging digital media experiences with content management and distribution platforms.",
    image: mediaImg,
    overview: "We create innovative media and entertainment solutions that captivate audiences and drive engagement. Our platforms support content creation, distribution, and monetization across multiple channels and devices.",
    challenges: [
      "Content delivery at global scale",
      "Multi-platform content distribution",
      "Digital rights management",
      "Audience engagement and retention",
      "Monetization optimization"
    ],
    solutions: [
      "Content Management Systems",
      "Video Streaming Platforms",
      "Digital Publishing Solutions",
      "Audience Analytics",
      "Content Distribution Networks",
      "Monetization Platforms"
    ],
    technologies: ["CDN", "Video Encoding", "DRM", "CMS", "Adobe Creative Suite", "AWS Media Services", "AI/ML"],
    benefits: [
      "Global content reach and accessibility",
      "60% improvement in viewer engagement",
      "Enhanced content security",
      "Better audience insights",
      "Increased revenue streams"
    ],
    caseStudies: [
      {
        title: "Global Streaming Platform",
        description: "Built scalable streaming service for international broadcaster",
        results: ["10M+ concurrent viewers", "99.9% stream reliability", "40% increase in subscriber retention"]
      },
      {
        title: "Digital Publishing Revolution",
        description: "Modernized content management for major media house",
        results: ["300% faster content publishing", "50% increase in reader engagement", "35% growth in digital subscriptions"]
      }
    ],
    stats: [
      { label: "Content Streams", value: "1B+" },
      { label: "Global Reach", value: "150+ Countries" },
      { label: "Uptime", value: "99.95%" },
      { label: "Engagement Increase", value: "60%" }
    ]
  },
  energy: {
    // icon: Energy,
    title: "Energy",
    // category: "Energy & Utilities",
    description: "Optimize energy operations with smart grid solutions and renewable energy management systems.",
    image: energyImg,
    overview: "We deliver innovative energy and utilities solutions that optimize operations, enhance sustainability, and improve customer service. Our expertise spans smart grid management, renewable energy integration, and regulatory compliance.",
    challenges: [
      "Aging infrastructure and modernization needs",
      "Integration of renewable energy sources",
      "Grid stability and reliability",
      "Regulatory compliance and reporting",
      "Customer demand for smart energy solutions"
    ],
    solutions: [
      "Smart Grid Management Systems",
      "Renewable Energy Integration",
      "Energy Trading Platforms",
      "Asset Management Solutions",
      "Customer Energy Portals",
      "Predictive Maintenance Systems"
    ],
    technologies: ["SCADA", "IoT Sensors", "Blockchain", "AI/ML", "GIS", "Meter Data Management", "Smart Meters"],
    benefits: [
      "Improved grid reliability and efficiency",
      "30% reduction in operational costs",
      "Enhanced renewable energy integration",
      "Better demand forecasting and management",
      "Improved customer satisfaction"
    ],
    caseStudies: [
      {
        title: "Smart Grid Implementation",
        description: "Deployed smart grid solution for regional utility serving 2M customers",
        results: ["25% reduction in outages", "40% improvement in energy efficiency", "99.9% grid reliability"]
      },
      {
        title: "Renewable Energy Management",
        description: "Built integrated platform for wind and solar energy management",
        results: ["50% increase in renewable capacity", "30% reduction in carbon footprint", "95% forecast accuracy"]
      }
    ],
    stats: [
      { label: "Energy Projects", value: "4+" },
      { label: "Smart Meters Managed", value: "10M+" },
      { label: "Grid Reliability", value: "99.9%" },
      { label: "Cost Savings", value: "30%" }
    ]
  },
  automotive: {
    // icon: Car,
    title: "Automotive",
    // category: "Automotive & Transportation",
    description: "Drive innovation in automotive industry with connected vehicle solutions and manufacturing systems.",
    image: automotiveImg,
    overview: "Our automotive solutions encompass connected vehicles, smart manufacturing, and digital transformation initiatives. We help automotive companies innovate with IoT, AI, and advanced manufacturing technologies.",
    challenges: [
      "Connected vehicle technology integration",
      "Autonomous driving development",
      "Supply chain complexity",
      "Manufacturing process optimization",
      "Customer experience digitization"
    ],
    solutions: [
      "Connected Vehicle Platforms",
      "Fleet Management Systems",
      "Manufacturing Execution Systems",
      "Dealer Management Solutions",
      "Telematics and Diagnostics",
      "Customer Experience Platforms"
    ],
    technologies: ["IoT", "V2X Communication", "AUTOSAR", "CAN Bus", "OBD-II", "Telematics", "AI/ML", "Edge Computing"],
    benefits: [
      "Enhanced vehicle connectivity and safety",
      "Improved manufacturing efficiency",
      "Better customer experience and service",
      "Reduced operational costs",
      "Faster time-to-market for new features"
    ],
    caseStudies: [
      {
        title: "Connected Vehicle Platform",
        description: "Developed comprehensive connected car solution for major OEM",
        results: ["5M+ connected vehicles", "40% improvement in diagnostics", "95% customer satisfaction"]
      },
      {
        title: "Smart Manufacturing System",
        description: "Implemented Industry 4.0 solution for automotive plant",
        results: ["20% increase in production efficiency", "50% reduction in defects", "30% lower maintenance costs"]
      }
    ],
    stats: [
      { label: "Connected Vehicles", value: "5M+" },
      { label: "Manufacturing Plants", value: "50+" },
      { label: "Uptime Improvement", value: "25%" },
      { label: "Customer Satisfaction", value: "4.8/5" }
    ]
  },
  education: {
    // icon: GraduationCap,
    title: "Education",
    // category: "Education Technology",
    description: "Transform learning experiences with digital education platforms and student management systems.",
    image: educationImg,
    overview: "We create innovative educational technology solutions that enhance learning outcomes, streamline administration, and improve student engagement. Our platforms support both traditional and online learning environments.",
    challenges: [
      "Digital transformation of traditional classrooms",
      "Student engagement in online learning",
      "Administrative process automation",
      "Integration with existing educational systems",
      "Accessibility and inclusive design"
    ],
    solutions: [
      "Learning Management Systems (LMS)",
      "Student Information Systems (SIS)",
      "Virtual Classroom Platforms",
      "Online Assessment Tools",
      "Educational Content Management",
      "Student Analytics and Reporting"
    ],
    technologies: ["Moodle", "Canvas", "Blackboard", "Zoom", "Microsoft Teams", "Google Workspace", "AI/ML", "Analytics"],
    benefits: [
      "Improved student engagement and outcomes",
      "Streamlined administrative processes",
      "Enhanced accessibility and inclusion",
      "Better parent and teacher communication",
      "Data-driven educational insights"
    ],
    caseStudies: [
      {
        title: "University Digital Transformation",
        description: "Implemented comprehensive LMS for 50,000+ students",
        results: ["80% increase in online engagement", "40% reduction in administrative tasks", "95% student satisfaction"]
      },
      {
        title: "K-12 District Platform",
        description: "Built integrated platform for 100+ schools",
        results: ["60% improvement in parent engagement", "30% increase in test scores", "99.9% system availability"]
      }
    ],
    stats: [
      { label: "Students Served", value: "2M+" },
      { label: "Educational Institutions", value: "80+" },
      { label: "Course Completion Rate", value: "85%" },
      { label: "System Uptime", value: "99.9%" }
    ]
  },
  "real-estate": {
    // icon: Home,
    title: "Real Estate",
    // category: "Real Estate Technology",
    description: "Revolutionize property management and real estate transactions with innovative digital solutions.",
    image: realEstateImg,
    overview: "Our real estate technology solutions streamline property management, enhance customer experiences, and optimize real estate operations. We help real estate professionals leverage technology for growth and efficiency.",
    challenges: [
      "Property data management and integration",
      "Customer relationship management",
      "Virtual property showcasing",
      "Transaction process automation",
      "Market analysis and reporting"
    ],
    solutions: [
      "Property Management Systems",
      "Real Estate CRM Platforms",
      "Virtual Tour and AR Solutions",
      "Transaction Management Tools",
      "Market Analytics Platforms",
      "Customer Portal Solutions"
    ],
    technologies: ["MLS Integration", "Zillow API", "Property Management Software", "VR/AR", "Blockchain", "AI/ML"],
    benefits: [
      "Streamlined property management processes",
      "Enhanced customer experience",
      "Improved sales and leasing efficiency",
      "Better market insights and analytics",
      "Reduced operational costs"
    ],
    caseStudies: [
      {
        title: "Property Management Platform",
        description: "Built comprehensive platform for 10,000+ properties",
        results: ["50% reduction in management time", "30% increase in tenant satisfaction", "25% improvement in occupancy rates"]
      },
      {
        title: "Real Estate Marketplace",
        description: "Developed online marketplace for property transactions",
        results: ["200% increase in property listings", "60% faster transaction times", "40% growth in user engagement"]
      }
    ],
    stats: [
      { label: "Properties Managed", value: "100K+" },
      { label: "Real Estate Agents", value: "5K+" },
      { label: "Transaction Volume", value: "$10B+" },
      { label: "Customer Satisfaction", value: "4.6/5" }
    ]
  },
  manufacturing: {
    // icon: Factory,
    title: "Manufacturing",
    // category: "Industrial Manufacturing",
    description: "Optimize manufacturing processes with Industry 4.0 solutions and smart factory technologies.",
    image: manufacturingImg,
    overview: "We deliver Industry 4.0 manufacturing solutions that optimize production, improve quality, and reduce costs. Our expertise spans smart factories, predictive maintenance, and digital transformation initiatives.",
    challenges: [
      "Legacy system modernization",
      "Production optimization and efficiency",
      "Quality control and assurance",
      "Supply chain integration",
      "Predictive maintenance implementation"
    ],
    solutions: [
      "Manufacturing Execution Systems (MES)",
      "Enterprise Resource Planning (ERP)",
      "IoT and Smart Factory Solutions",
      "Digital Twin Technology",
      "Predictive Maintenance Platforms",
      "Quality Management Systems"
    ],
    technologies: ["MES", "ERP", "IoT", "Digital Twin", "AI/ML", "Robotics", "SCADA", "Edge Computing"],
    benefits: [
      "Increased production efficiency",
      "Improved product quality",
      "Reduced operational costs",
      "Enhanced supply chain visibility",
      "Better predictive maintenance"
    ],
    caseStudies: [
      {
        title: "Smart Factory Implementation",
        description: "Transformed traditional factory into Industry 4.0 facility",
        results: ["35% increase in productivity", "50% reduction in defects", "40% lower maintenance costs"]
      },
      {
        title: "Digital Twin Deployment",
        description: "Implemented digital twin for complex manufacturing process",
        results: ["25% improvement in efficiency", "60% faster problem resolution", "30% reduction in waste"]
      }
    ],
    stats: [
      { label: "Manufacturing Plants", value: "48+" },
      { label: "Production Lines", value: "1K+" },
      { label: "Efficiency Improvement", value: "35%" },
      { label: "Cost Reduction", value: "40%" }
    ]
  },
  logistics: {
    // icon: Truck,
    title: "Logistics",
    // category: "Logistics & Transportation",
    description: "Streamline supply chain and logistics operations with intelligent tracking and management systems.",
    image: logisticsImg,
    overview: "Our logistics and supply chain solutions optimize operations, improve visibility, and enhance customer service. We help logistics companies leverage technology for efficiency and competitive advantage.",
    challenges: [
      "Real-time shipment tracking and visibility",
      "Route optimization and planning",
      "Warehouse management efficiency",
      "Supply chain integration",
      "Last-mile delivery optimization"
    ],
    solutions: [
      "Fleet Management Systems",
      "Warehouse Management Systems (WMS)",
      "Transportation Management Systems (TMS)",
      "Supply Chain Visibility Platforms",
      "Route Optimization Solutions",
      "Last-Mile Delivery Apps"
    ],
    technologies: ["GPS Tracking", "RFID", "Blockchain", "AI Optimization", "WMS", "TMS", "IoT", "Analytics"],
    benefits: [
      "Improved supply chain visibility",
      "Optimized routes and reduced costs",
      "Enhanced customer satisfaction",
      "Better inventory management",
      "Increased operational efficiency"
    ],
    caseStudies: [
      {
        title: "Global Supply Chain Platform",
        description: "Built end-to-end visibility platform for multinational logistics company",
        results: ["40% improvement in delivery times", "25% reduction in logistics costs", "99% shipment visibility"]
      },
      {
        title: "Last-Mile Delivery Optimization",
        description: "Developed AI-powered route optimization for urban delivery",
        results: ["30% reduction in delivery time", "20% lower fuel costs", "95% on-time delivery rate"]
      }
    ],
    stats: [
      { label: "Shipments Tracked", value: "10M+" },
      { label: "Fleet Vehicles", value: "50K+" },
      { label: "Cost Reduction", value: "25%" },
      { label: "Delivery Accuracy", value: "99%" }
    ]
  },
  entertainment: {
    // icon: Clapperboard,
    title: "Entertainment",
    // category: "Entertainment Industry",
    description: "Create immersive entertainment experiences with digital platforms and content delivery solutions.",
    image: entertainmentImg,
    overview: "We create cutting-edge entertainment technology solutions that captivate audiences and drive engagement. Our platforms support content creation, distribution, and monetization across multiple channels and platforms.",
    challenges: [
      "Multi-platform content distribution",
      "Audience engagement and retention",
      "Content monetization optimization",
      "Digital rights management",
      "Scalable streaming infrastructure"
    ],
    solutions: [
      "Streaming Platform Development",
      "Content Management Systems",
      "Audience Engagement Platforms",
      "Digital Rights Management (DRM)",
      "Monetization and Analytics",
      "Social Integration Solutions"
    ],
    technologies: ["Streaming Platforms", "DRM", "CDN", "Video Analytics", "Social Integration", "AI/ML", "Cloud Computing"],
    benefits: [
      "Enhanced audience engagement",
      "Improved content monetization",
      "Scalable content delivery",
      "Better audience insights",
      "Increased revenue streams"
    ],
    caseStudies: [
      {
        title: "Global Entertainment Platform",
        description: "Built multi-platform entertainment ecosystem for major studio",
        results: ["100M+ global users", "50% increase in engagement", "300% growth in revenue"]
      },
      {
        title: "Interactive Gaming Platform",
        description: "Developed social gaming platform with real-time features",
        results: ["10M+ active users", "80% user retention rate", "40% increase in in-app purchases"]
      }
    ],
    stats: [
      { label: "Global Users", value: "100M+" },
      { label: "Content Hours", value: "1M+" },
      { label: "Platform Uptime", value: "99.9%" },
      { label: "Revenue Growth", value: "200%" }
    ]
  },
  "consumer-goods": {
    // icon: Package,
    title: "Consumer Goods and Services",
    // category: "Consumer Products & Services",
    description: "Transform consumer product businesses with digital solutions for brand engagement and service delivery.",
    image: consumerGoodsImg,
    overview: "We empower consumer goods and services companies to build stronger brand connections, optimize operations, and enhance customer experiences through innovative digital solutions. Our expertise spans product lifecycle management, e-commerce platforms, customer engagement, and supply chain optimization.",
    challenges: [
      "Brand differentiation in competitive markets",
      "Direct-to-consumer channel development",
      "Supply chain complexity and transparency",
      "Customer experience personalization",
      "Omnichannel retail integration",
      "Sustainability and regulatory compliance"
    ],
    solutions: [
      "Product Lifecycle Management (PLM) Systems",
      "Direct-to-Consumer E-commerce Platforms",
      "Brand Management and Marketing Automation",
      "Customer Experience Personalization",
      "Supply Chain Visibility and Optimization",
      "Sustainability Tracking and Reporting",
      "Retail Partnership Integration",
      "Customer Service and Support Platforms"
    ],
    technologies: ["PLM Systems", "CRM Platforms", "E-commerce Solutions", "Marketing Automation", "Supply Chain Analytics", "IoT Sensors", "AI/ML", "Mobile Applications"],
    benefits: [
      "Enhanced brand visibility and customer loyalty",
      "40% improvement in time-to-market for new products",
      "Increased direct-to-consumer sales by 60%",
      "Better supply chain transparency and efficiency",
      "Improved customer satisfaction and retention",
      "Reduced operational costs through automation"
    ],
    caseStudies: [
      {
        title: "Global Beauty Brand Digital Transformation",
        description: "Implemented comprehensive digital ecosystem for international beauty and personal care brand",
        results: ["300% increase in online sales", "50% improvement in customer engagement", "25% faster product launches"]
      },
      {
        title: "Consumer Electronics PLM Platform",
        description: "Built integrated product lifecycle management system for electronics manufacturer",
        results: ["40% reduction in product development time", "60% improvement in quality control", "35% cost savings in operations"]
      }
    ],
    stats: [
      { label: "Consumer Brands Served", value: "150+" },
      { label: "Products Launched", value: "10K+" },
      { label: "Customer Engagement Increase", value: "75%" },
      { label: "Supply Chain Efficiency", value: "45%" }
    ]
  },
  utilities: {
    // icon: Power,
    title: "Utilities",
    // category: "Utilities",
    description: "Modernize utility operations with smart infrastructure, billing systems, and customer service platforms.",
    image: utilitiesImg,
    overview: "We deliver comprehensive utility solutions that modernize operations, improve customer service, and optimize infrastructure management. Our expertise spans smart grid technologies, utility billing systems, customer information systems, and regulatory compliance solutions for electric, gas, water, and renewable energy providers.",
    challenges: [
      "Aging infrastructure requiring modernization",
      "Regulatory compliance and reporting requirements",
      "Customer service and billing complexity",
      "Grid reliability and outage management",
      "Integration of renewable energy sources",
      "Cybersecurity threats to critical infrastructure"
    ],
    solutions: [
      "Smart Grid Management and Analytics",
      "Advanced Utility Billing Systems",
      "Customer Information Systems (CIS)",
      "Outage Management Systems (OMS)",
      "Asset Management and Maintenance",
      "Regulatory Compliance and Reporting",
      "Mobile Workforce Management",
      "Customer Self-Service Portals"
    ],
    technologies: ["Smart Meters", "SCADA Systems", "GIS Mapping", "CIS Platforms", "Meter Data Management", "Grid Analytics", "IoT Sensors", "Mobile Applications"],
    benefits: [
      "Improved grid reliability and efficiency",
      "Enhanced customer service and satisfaction",
      "Reduced operational costs by up to 30%",
      "Better regulatory compliance and reporting",
      "Faster outage detection and restoration",
      "Optimized asset management and maintenance"
    ],
    caseStudies: [
      {
        title: "Smart Grid Modernization Program",
        description: "Implemented comprehensive smart grid solution for regional utility serving 800K customers",
        results: ["45% reduction in outage duration", "30% improvement in grid efficiency", "25% decrease in operational costs"]
      },
      {
        title: "Unified Customer Information System",
        description: "Deployed integrated CIS platform for multi-service utility company",
        results: ["60% improvement in customer service response", "40% reduction in billing errors", "50% faster new service connections"]
      }
    ],
    stats: [
      { label: "Utility Companies Served", value: "25+" },
      { label: "Smart Meters Deployed", value: "800+" },
      { label: "Grid Reliability", value: "99.8%" },
      { label: "Customer Satisfaction", value: "4.5/5" }
    ]
  }
};

const IndustryDetail = () => {
  const { industryId } = useParams<{ industryId: string }>();
  
  // Force re-render when industryId changes
  useEffect(() => {
    // Scroll to top immediately and after a short delay to ensure it works
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [industryId]);
  
  if (!industryId || !industriesData[industryId]) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="container mx-auto px-4 py-20">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Industry Not Found</h1>
            <p className="text-muted-foreground mb-8">The requested industry page could not be found.</p>
            <Link to="/">
              {/* <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button> */}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const industry = industriesData[industryId];
  // const IconComponent = industry.icon;

  return (
    <div className="min-h-screen">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={industry.image} 
            alt={industry.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl">
            {/* <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link> */}
            
            {/* <Badge variant="secondary" className="mb-4">
              {industry.category}
            </Badge> */}
            
            <div className="flex items-center gap-4 mb-6">
              {/* <div className="p-3 bg-primary/20 rounded-lg backdrop-blur-sm">
                <IconComponent className="h-8 w-8 text-white" />
              </div> */}
              <h1 className="text-4xl lg:text-6xl font-bold text-white  mt-12">
                {industry.title}
              </h1>
            </div>
            
            <p className="text-xl text-white/90 mb-8 max-w-3xl">
              {industry.description}
            </p>
            
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {industry.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Industry Overview</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {industry.overview}
            </p>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Challenges */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-destructive" />
                  Industry Challenges
                </CardTitle>
                <CardDescription>
                  Key challenges we help our clients overcome
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {industry.challenges.map((challenge, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-destructive rounded-full mt-2 flex-shrink-0" />
                      <span className="text-sm">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Solutions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  Our Solutions
                </CardTitle>
                <CardDescription>
                  How we address these challenges
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {industry.solutions.map((solution, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{solution}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
              <Zap className="h-6 w-6 text-primary" />
              Technologies We Use
            </h2>
            <div className="flex flex-wrap gap-2">
              {industry.technologies.map((tech, index) => (
                <Badge key={index} variant="secondary" className="text-sm py-1">
                  {tech}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary" />
              Key Benefits
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {industry.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-background rounded-lg">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-2">
              <Users className="h-6 w-6 text-primary" />
              Success Stories
            </h2>
            <div className="grid lg:grid-cols-2 gap-8">
              {industry.caseStudies.map((caseStudy, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-xl">{caseStudy.title}</CardTitle>
                    <CardDescription>{caseStudy.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-semibold mb-3">Key Results:</h4>
                    <ul className="space-y-2">
                      {caseStudy.results.map((result, resultIndex) => (
                        <li key={resultIndex} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustryDetail;